import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import Select from 'react-select';
import { getTournamentRegistrationTransactions } from '../../../api/paymentTransactions/transactions';
import '../PaymentTransactions.css';
import InvoiceModal from '../../SuperAdminPayments/components/InvoiceModal';
import { Transaction } from './types';
import { PaymentTransaction } from '../../../api/superAdminPayments/paymentsService';
import { downloadUnifiedInvoice, generateUnifiedInvoiceBlob, getInvoiceEmailRecipient, generateInvoiceEmailSubject, generateInvoiceEmailBody } from '../../../utils/unifiedInvoiceService';
import SharedDownloadOverlay from '../../../components/SharedDownloadOverlay';
import ShimmerRow from '../../../components/ShimmerRow';
import TransactionTable from '../../../components/TransactionTable';

const Transactions: React.FC = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: 'All',
    paymentMethod: 'All',
    dateRange: 'All'
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Invoice modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | PaymentTransaction | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDownloadOverlay, setShowDownloadOverlay] = useState(false);

  // Filter options - Updated to match API response values
  const statusOptions = [
    { value: 'All', label: 'All' },
    { value: 'paid', label: 'Success' },
    { value: 'failed', label: 'Failed' },
    { value: 'pending', label: 'Pending' }
  ];

  const paymentMethodOptions = [
    { value: 'All', label: 'All' },
    { value: 'razorpay', label: 'Razorpay' },
    { value: 'card', label: 'Card' },
    { value: 'upi', label: 'UPI' },
    { value: 'netbanking', label: 'Net Banking' }
  ];

  const dateRangeOptions = [
    { value: 'All', label: 'All Time' },
    { value: 'Today', label: 'Today' },
    { value: 'Week', label: 'This Week' },
    { value: 'Month', label: 'This Month' },
    { value: 'Year', label: 'This Year' }
  ];

  // Load initial data
  useEffect(() => {
    loadTransactions(1).finally(() => {
      setIsInitialLoad(false); // Mark initial load as complete
    });
  }, []);

  // Load data when page changes
  useEffect(() => {
    if (!isInitialLoad) {
      loadTransactions(currentPage);
    }
  }, [currentPage]);

  // Reload data when filters change
  useEffect(() => {
    if (currentPage === 1) {
      loadTransactions(1);
    } else {
      setCurrentPage(1);
    }
  }, [filters]);

  const loadTransactions = async (page: number = 1) => {
    try {
      setIsPageLoading(true);
      setError(null);
      
      // Prepare filters for API call - Map to API field names
      const apiFilters: any = {};
      if (filters.status !== 'All') apiFilters.status = filters.status;
      if (filters.paymentMethod !== 'All') apiFilters.paymentMethod = filters.paymentMethod;
      if (filters.dateRange !== 'All') apiFilters.dateRange = filters.dateRange;
      
      try {
        const response = await getTournamentRegistrationTransactions(page, apiFilters);
        console.log('Tournament registration transactions response:', response);
        console.log('API Filters applied:', apiFilters);
        
        // Handle the API response structure: response.data.paginatedResults
        if (response && response.data && response.data.paginatedResults) {
          // Debug: Log the first transaction to see available fields
          if (response.data.paginatedResults.length > 0) {
            console.log('🔍 API Response - First transaction fields:', Object.keys(response.data.paginatedResults[0]));
            console.log('🔍 API Response - First transaction data:', response.data.paginatedResults[0]);
            console.log('🔍 Age Category Debug:', {
              tournamentInfo_ageCategory: response.data.paginatedResults[0].tournamentInfo?.ageCategory,
              direct_ageCategory: response.data.paginatedResults[0].ageCategory,
              teamInfo_ageCategory: response.data.paginatedResults[0].teamInfo?.ageCategory
            });
          }
          
          // Map API response to Transaction interface
          const mappedTransactions: Transaction[] = response.data.paginatedResults.map((item: any) => ({
            _id: item._id,
            transactionId: item.paymentId || `TXN_${item._id.slice(-6)}`, // Use paymentId or generate from _id
            paymentId: item.paymentId || item.razorpayPaymentId || item.razorpayOrderId, // ✅ Add paymentId field for consistent display
            tournamentName: item.tournamentInfo?.name || 'Unknown Tournament',
            team: item.teamInfo?.shortName || item.teamInfo?.name || 'Unknown Team',
            ageCategory: item.tournamentInfo?.ageCategory || item.ageCategory || item.teamInfo?.ageCategory || 'N/A',
            amount: item.amount || 0,
            paymentMethod: item.method || 'Unknown',
            status: item.paidStatus === 'paid' ? 'Success' : 
                   item.paidStatus === 'failed' ? 'Failed' : 'Pending',
            date: item.paidAt || item.createdAt || new Date().toISOString(),
            description: `Payment for ${item.milestone || 'tournament registration'}`,
            razorpayOrderId: item.razorpayOrderId || item.paymentId,
            razorpayPaymentId: item.razorpayPaymentId || item.paymentId,
            type: item.milestone || 'tournament_registration', // Required for PaymentTransaction compatibility
            tournamentInfo: {
              _id: item.tournamentInfo?._id || item._id,
              name: item.tournamentInfo?.name || 'Unknown Tournament',
              shortName: item.tournamentInfo?.shortName || 'Unknown',
              logo: item.tournamentInfo?.logo || '',
              ageCategory: item.tournamentInfo?.ageCategory || item.ageCategory || item.teamInfo?.ageCategory || null
            },
            teamInfo: {
              _id: item.teamInfo?._id || item._id,
              name: item.teamInfo?.name || 'Unknown Team',
              shortName: item.teamInfo?.shortName || 'Unknown',
              logo: item.teamInfo?.logo || '',
              ageCategory: item.teamInfo?.ageCategory || item.ageCategory || 'Unknown',
              clubInfo: {
                _id: item.teamInfo?.clubInfo?._id || item.clubInfo?._id || '',
                name: item.teamInfo?.clubInfo?.name || item.clubInfo?.name || '',
                logo: item.teamInfo?.clubInfo?.logo || item.clubInfo?.logo || '',
                address: item.teamInfo?.clubInfo?.address || item.clubInfo?.address || null,
                gstNum: item.teamInfo?.clubInfo?.gstNum || item.clubInfo?.gstNumber || null
              }
            },
            playersCount: item.playersCount || 1,
            paidStatus: item.paidStatus || 'pending',
            method: item.method || 'Unknown',
            paidAt: item.paidAt || item.createdAt || new Date().toISOString(),
            createdAt: item.createdAt || new Date().toISOString(),
            milestone: item.milestone || 'tournament registration',
            userDetails: {
              name: item.teamInfo?.shortName || item.teamInfo?.name || 'Unknown',
              email: '',
              phone: ''
            }
          }));
          
          setTransactions(mappedTransactions);
          setTotalCount(response.data.totalCount || 0);
          setTotalPages(response.data.totalPages || Math.ceil((response.data.totalCount || 0) / 10));
          console.log('Transactions loaded:', mappedTransactions);
          console.log('Total count:', response.data.totalCount, 'Total pages:', response.data.totalPages);
        } else if (response && response.paginatedResults) {
          // Fallback for direct response structure
          setTransactions(response.paginatedResults);
          setTotalCount(response.totalCount || 0);
          setTotalPages(response.totalPages || 1);
          console.log('Transactions loaded (direct):', response.paginatedResults);
        } else {
          console.log('No transactions found from API');
          setTransactions([]);
          setTotalCount(0);
          setTotalPages(0);
          setError(null); // Don't set error for empty data
        }
      } catch (apiError: any) {
        console.error('Failed to load transactions from API:', apiError);
        
        // Handle specific error cases
        if (apiError.response?.status === 404) {
          // 404 means no transactions found - this is not an error, just empty data
          setTransactions([]);
          setTotalCount(0);
          setTotalPages(0);
          setError(null); // Don't set error for empty data
        } else if (apiError.response?.status === 403) {
          setError('Access denied. Please check your permissions.');
          setTransactions([]);
          setTotalCount(0);
          setTotalPages(0);
        } else if (apiError.response?.data?.error) {
          setError(apiError.response.data.error);
          setTransactions([]);
          setTotalCount(0);
          setTotalPages(0);
        } else {
          setError('Failed to load transactions. Please try again later.');
          setTransactions([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      }
    } catch (error: any) {
      console.error('Error loading transactions:', error);
      setError('An unexpected error occurred. Please try again later.');
      setTransactions([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setIsPageLoading(false);
      setLoading(false); // Also set main loading to false
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // Reset to page 1 when filters change
    setCurrentPage(1);
  };

  const handlePageChange = async (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }
    
    setCurrentPage(page);
    
    // Scroll to top for better UX
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Load transactions for the new page
    await loadTransactions(page);
  };

  // Invoice modal handlers
  const handleViewInvoice = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowInvoiceModal(true);
  };

  const handleDownloadInvoice = async (transaction: Transaction) => {
    try {
      setIsDownloading(true);
      setShowDownloadOverlay(true);
      
      console.log('Starting invoice download for transaction:', transaction._id);
      
      // Convert Transaction to unified format for invoice generation
      const unifiedTransaction = {
        _id: transaction._id,
        amount: transaction.amount,
        method: transaction.method,
        milestone: transaction.milestone,
        type: transaction.type,
        playersCount: transaction.playersCount,
        paidStatus: transaction.paidStatus,
        paidAt: transaction.paidAt,
        createdAt: transaction.createdAt,
        tournamentInfo: {
          _id: transaction.tournamentInfo._id,
          name: transaction.tournamentInfo.name,
          shortName: transaction.tournamentInfo.shortName,
          logo: transaction.tournamentInfo.logo,
          ageCategory: transaction.tournamentInfo.ageCategory
        },
        teamInfo: {
          _id: transaction.teamInfo._id,
          name: transaction.teamInfo.name,
          shortName: transaction.teamInfo.shortName,
          logo: transaction.teamInfo.logo,
          ageCategory: transaction.teamInfo.ageCategory,
          clubInfo: transaction.teamInfo.clubInfo
        }
      };
      
      console.log('Converted unified transaction:', unifiedTransaction);
      
      // Generate PDF directly without opening modal - Layout protection is handled by unified service
      await generatePDFDirectly(unifiedTransaction);
      
      console.log('Invoice download completed successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      // You can add a toast notification here
      alert('Failed to download invoice. Please try again.');
    } finally {
      setIsDownloading(false);
      setShowDownloadOverlay(false);
    }
  };

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedTransaction(null);
  };

  // PDF generation function using unified service
  const generatePDFDirectly = async (transaction: any) => {
    try {
      console.log('Starting unified PDF generation for Club Manager transaction:', transaction._id);
      
      // Use unified service to generate PDF
      await downloadUnifiedInvoice(transaction);
      
      console.log('Unified PDF generation completed successfully');
    } catch (error) {
      console.error('Error generating unified PDF:', error);
      throw new Error('Failed to generate PDF invoice');
    }
  };

  // Email function using unified service
  const handleEmailInvoice = async (transaction: Transaction) => {
    try {
      console.log('Starting unified email invoice for Club Manager transaction:', transaction._id);
      
      // Generate PDF blob using unified service
      const pdfBlob = await generateUnifiedInvoiceBlob(transaction);
      
      // Get email details from unified service
      const recipientEmail = getInvoiceEmailRecipient(transaction);
      const subject = generateInvoiceEmailSubject(transaction);
      const body = generateInvoiceEmailBody(transaction);
      
      // Create filename
      const tournamentName = (transaction.tournamentInfo?.name || 'Tournament').replace(/\s+/g, '-');
      const teamName = (transaction.teamInfo?.name || 'Team').replace(/\s+/g, '-');
      const filename = `invoice-${tournamentName}-${teamName}.pdf`;
      
      // Create a download link for the PDF
      const downloadUrl = URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = filename;
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      
      // Create email body with clear instructions
      const encodedSubject = encodeURIComponent(subject);
      const encodedBody = encodeURIComponent(body);
      
      // Open Gmail compose window
      const mailtoLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${recipientEmail || ''}&su=${encodedSubject}&body=${encodedBody}`;
      window.open(mailtoLink, '_blank');
      
      // Automatically trigger PDF download
      downloadLink.click();
      
      // Clean up
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadUrl);
      
      // Show success message
      alert(`PDF downloaded successfully as ${filename}!\n\nGmail has been opened. Please manually attach the downloaded PDF to your email.`);
      
      console.log('Unified email invoice completed successfully');
    } catch (error) {
      console.error('Error sending unified email invoice:', error);
      alert('Failed to send email invoice. Please try again.');
    }
  };


  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Success':
      case 'paid':
        return <Badge bg="success" className="payment-badge">Success</Badge>;
      case 'Failed':
      case 'failed':
        return <Badge bg="danger" className="payment-badge">Failed</Badge>;
      case 'Pending':
      case 'pending':
        return <Badge bg="warning" className="payment-badge">Pending</Badge>;
      default:
        return <Badge bg="secondary" className="payment-badge">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading || isPageLoading) {
    return (
      <div className="transactions-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          {/* Breadcrumbs Shimmer */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <div className="shimmer-text" style={{ width: '300px', height: '24px' }}></div>
          </div>
          
          {/* Page Header Shimmer */}
          <div className="d-flex justify-content-between align-items-center mb-4" style={{ padding: '0 15px' }}>
            <div>
              <div className="shimmer-text" style={{ width: '250px', height: '32px', marginBottom: '8px' }}></div>
              <div className="shimmer-text" style={{ width: '350px', height: '16px' }}></div>
            </div>
          </div>

          {/* Filters Shimmer - Commented out for now */}
          {/* <Card className="mb-4">
            <Card.Body>
              <Row>
                <Col md={4}>
                  <div className="shimmer-text" style={{ width: '80px', height: '16px', marginBottom: '8px' }}></div>
                  <div className="shimmer-text" style={{ width: '100%', height: '45px' }}></div>
                </Col>
                <Col md={4}>
                  <div className="shimmer-text" style={{ width: '120px', height: '16px', marginBottom: '8px' }}></div>
                  <div className="shimmer-text" style={{ width: '100%', height: '45px' }}></div>
                </Col>
                <Col md={4}>
                  <div className="shimmer-text" style={{ width: '100px', height: '16px', marginBottom: '8px' }}></div>
                  <div className="shimmer-text" style={{ width: '100%', height: '45px' }}></div>
                </Col>
              </Row>
            </Card.Body>
          </Card> */}

          {/* Table Shimmer */}
          <Card>
            <Card.Body>
              <div className="shimmer-table">
                <div className="shimmer-header">
                  {[...Array(9)].map((_, index) => (
                    <div key={index} className="shimmer-cell" />
                  ))}
                </div>
                <div className="shimmer-body">
                  {[...Array(5)].map((_, rowIndex) => (
                    <div key={rowIndex} className="shimmer-row">
                      {[...Array(9)].map((_, colIndex) => (
                        <div key={colIndex} className="shimmer-cell" />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="transactions-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              {
                label: 'Payment & Transactions',
                active: false
              },
              {
                label: 'Transactions',
                active: true
              }
            ]} 
          />
        </div>

        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4" style={{ padding: '0 15px' }}>
          <div>
            <h4 className="mb-2">Payment Transactions ({totalCount})</h4>
            <p className="text-muted mb-0">View all payment transaction history</p>
          </div>
        </div>

        {/* Filters - Commented out for now */}
        {/* <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={statusOptions.find(option => option.value === filters.status)}
                    onChange={(selectedOption) => handleFilterChange('status', selectedOption?.value || 'All')}
                    options={statusOptions}
                    placeholder="Select status"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '45px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Method</Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={paymentMethodOptions.find(option => option.value === filters.paymentMethod)}
                    onChange={(selectedOption) => handleFilterChange('paymentMethod', selectedOption?.value || 'All')}
                    options={paymentMethodOptions}
                    placeholder="Select payment method"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '45px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Date Range</Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={dateRangeOptions.find(option => option.value === filters.dateRange)}
                    onChange={(selectedOption) => handleFilterChange('dateRange', selectedOption?.value || 'All')}
                    options={dateRangeOptions}
                    placeholder="Select date range"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '45px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card> */}

        {/* Transactions Table */}
        {error ? (
          <Card>
            <Card.Body>
              <div className="text-center py-5">
                <div className="mb-3">
                  <i className="ri-error-warning-line text-danger" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-danger mb-3">Unable to Load Transactions</h5>
                <p className="text-muted mb-4">{error}</p>
                <Button variant="primary" onClick={() => loadTransactions(currentPage)}>
                  <i className="ri-refresh-line me-2"></i>
                  Retry
                </Button>
              </div>
            </Card.Body>
          </Card>
        ) : transactions.length === 0 && !isPageLoading ? (
          <Card>
            <Card.Body>
              <div className="text-center py-5">
                <div className="mb-3">
                  <i className="ri-file-list-3-line text-muted" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-muted mb-3">No Transactions Found</h5>
                <p className="text-muted mb-4">You don't have any payment transactions yet.</p>
                <Button variant="outline-primary" onClick={() => navigate('/tournaments')}>
                  <i className="ri-add-line me-2"></i>
                  Register for a Tournament
                </Button>
              </div>
            </Card.Body>
          </Card>
        ) : (
          <TransactionTable
            transactions={transactions}
            loading={isPageLoading}
            onViewTransaction={handleViewInvoice}
            showActions={true}
            showHeader={false}
            isDashboard={false}
          />
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && transactions.length > 0 && !error && (
          <div className="pagination-section mt-4" style={{ 
            padding: '16px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Page Info */}
            <div className="page-info mb-3">
              <span className="text-muted" style={{ 
                fontSize: '14px', 
                fontWeight: '500',
                color: '#6c757d'
              }}>
                Showing {((currentPage - 1) * 10) + 1} - {Math.min(currentPage * 10, totalCount)} of {totalCount} transactions
              </span>
            </div>

            {/* Pagination Controls */}
            <div className="pagination-controls d-flex align-items-center justify-content-center">
              {/* Previous Button */}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isPageLoading}
                style={{ 
                  minWidth: '90px', 
                  height: '36px',
                  fontSize: '13px',
                  borderColor: currentPage === 1 ? '#e9ecef' : '#ced4da',
                  backgroundColor: currentPage === 1 ? '#f8f9fa' : '#ffffff',
                  color: currentPage === 1 ? '#adb5bd' : '#495057',
                  borderRadius: '6px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="ri-arrow-left-s-line me-1"></i>
                Previous
              </Button>

              {/* Page Numbers */}
              <div className="d-flex align-items-center mx-3">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      disabled={isPageLoading}
                      style={{ 
                        minWidth: '36px', 
                        height: '36px',
                        fontSize: '13px',
                        margin: '0 2px',
                        borderRadius: '6px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              {/* Next Button */}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isPageLoading}
                style={{ 
                  minWidth: '90px', 
                  height: '36px',
                  fontSize: '13px',
                  borderColor: currentPage === totalPages ? '#e9ecef' : '#ced4da',
                  backgroundColor: currentPage === totalPages ? '#f8f9fa' : '#ffffff',
                  color: currentPage === totalPages ? '#adb5bd' : '#495057',
                  borderRadius: '6px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                Next
                <i className="ri-arrow-right-s-line ms-1"></i>
              </Button>
            </div>
          </div>
        )}

        {/* Invoice Modal */}
        <InvoiceModal 
          show={showInvoiceModal}
          onHide={handleCloseInvoiceModal}
          transaction={selectedTransaction}
          generatePDF={true}
        />

        {/* Download Overlay */}
        <SharedDownloadOverlay 
          isVisible={showDownloadOverlay} 
          message="Generating Invoice..." 
        />
      </Container>
    </div>
  );
};

export default Transactions; 